(function () {
  'use strict';

  function fingerprint(item) {
    return JSON.stringify([
      String(item.variant_id || item.id),
      Object.entries(item.properties || {}).sort(([a], [b]) => a.localeCompare(b)),
      String(item.selling_plan_allocation?.selling_plan?.id || ''),
      item.original_price
    ]);
  }

  function createSerialGate() {
    let tail = Promise.resolve();
    let count = 0;
    return {
      run(task) {
        count += 1;
        const result = tail.then(task);
        tail = result.catch(() => {}).finally(() => { count -= 1; });
        return tail.then(() => result);
      },
      pending: () => count > 0,
      idle: () => tail
    };
  }

  function createQuantityQueue(io) {
    const exclusive = io.exclusive || (task => task());
    const entries = new Map();
    const knownItems = new Map();
    let timer = null;
    let running = false;
    let revision = 0;
    let waiters = [];
    const dirty = () => [...new Set(entries.values())].find(e => e.desired !== e.saved);
    const pending = () => running || Boolean(dirty());
    const notify = () => io.pending?.([...new Set(entries.values())]);
    const fail = () => { throw new Error('cart_changed'); };
    function remember(cart) {
      (cart.items || []).forEach(item => knownItems.set(item.key, fingerprint(item)));
    }

    function update(key, shown, delta, remove) {
      let entry = entries.get(key);
      if (!entry) {
        if (!key || !key.includes(':') || !Number.isSafeInteger(shown) || shown < 1) return false;
        entry = { key, desired: shown, saved: shown, version: 0 };
        entries.set(key, entry);
      }
      const desired = remove ? 0 : Math.max(1, entry.desired + delta);
      if (!Number.isSafeInteger(desired)) return false;
      if (desired === entry.desired) return true;
      entry.desired = desired;
      entry.version += 1;
      revision += 1;
      notify();
      clearTimeout(timer);
      timer = setTimeout(drain, io.delay ?? 160);
      return true;
    }

    async function drain() {
      let activeKey;
      clearTimeout(timer);
      if (running) return;
      running = true;
      try {
        let entry;
        while ((entry = dirty())) {
          activeKey = entry.key;
          await exclusive(async () => {
          const cart = await io.read();
          let item = cart.items?.find(i => i.key === entry.key);
          if (!item) {
            const previous = knownItems.get(entry.key);
            const matches = previous === undefined ? [] : cart.items.filter(i => fingerprint(i) === previous);
            if (matches.length !== 1) fail();
            item = matches[0];
            entry.key = item.key;
            activeKey = entry.key;
            entries.set(item.key, entry);
          }
          remember(cart);
          const wanted = entry.desired;
          const sentVersion = entry.version;
          if (item.quantity === wanted) {
            entry.saved = wanted;
            return;
          }
          const result = await io.write(item.key, wanted);
          if (!Array.isArray(result.items)) fail();
          remember(result);
          let next = result.items.find(i => i.key === item.key);
          if (!next && wanted > 0) {
            const matches = result.items.filter(i => fingerprint(i) === fingerprint(item));
            if (matches.length !== 1) fail();
            next = matches[0];
          }
          if (wanted === 0 && next) fail();
          if (next) {
            entry.key = next.key;
            entries.set(next.key, entry);
          }
          entry.saved = next ? next.quantity : 0;
          if (entry.saved !== wanted) {
            if (entry.version === sentVersion) entry.desired = entry.saved;
            io.error?.('quantity', entry.saved, entry.key);
          }
          notify();
          });
        }
      } catch (error) {
        entries.clear();
        revision += 1;
        io.error?.(error, undefined, activeKey);
      } finally {
        clearTimeout(timer);
        running = false;
        const done = waiters;
        waiters = [];
        done.forEach(resolve => resolve());
        io.idle?.();
      }
    }

    return {
      remember,
      adjust: (key, shown, delta) => update(key, shown, delta, false),
      remove: (key, shown) => update(key, shown, 0, true),
      flush: async () => { await drain(); if (pending()) await new Promise(resolve => waiters.push(resolve)); },
      pending,
      revision: () => revision,
      idle: () => pending() ? new Promise(resolve => waiters.push(resolve)) : Promise.resolve(),
      overlay: key => entries.get(key)?.desired,
      release: version => { if (!pending() && revision === version) entries.clear(); }
    };
  }

  function createReturnSynchronizer(io) {
    let pending = null;
    return {
      sync() {
        if (pending) return pending;
        io.block(true);
        pending = (async () => {
          for (let attempt = 0; attempt < 3; attempt += 1) {
            if (await io.refresh()) { io.success?.(); return true; }
            if (io.pause && attempt < 2) await io.pause();
          }
          io.defer?.();
          return false;
        })().catch(error => { io.error(error); return false; })
          .finally(() => { io.block(false); pending = null; });
        return pending;
      }
    };
  }

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = { createQuantityQueue, createReturnSynchronizer, createSerialGate };
    return;
  }
  if (window.MBICart || window.MBIMarket?.ecommerceEnabled !== true) return;

  const VERSION = '2026-11-03.cart8-scroll-lock';
  const rowSelector = '#mini-cart .cart-item, #cart .cart-item';
  const root = window.Shopify?.routes?.root || '/';
  const cartUrl = window.routes?.cart || `${root}cart`;
  const genericError = () => window.cartStrings?.error || window.cartStrings?.unavailable || '';
  let timer;
  let renderRevision = 0;
  let mutationRevision = 0;
  let mutations = 0;
  let shouldOpen = false;
  let errorText = '';
  let errorKey = null;
  let rendering = null;
  let lifecycleRevision = 0;

  function showError(value, key = null) {
    errorText = value;
    errorKey = value ? key : null;
    document.querySelectorAll('#mini-cart .cart-item__error, #cart .cart-item__error, [data-mbi-cart-error]').forEach(message => {
      message.textContent = '';
      message.hidden = true;
    });
    if (!value) return;
    ['#mini-cart .mini-cart__sidebar', '#cart'].forEach(selector => {
      const container = document.querySelector(selector);
      if (!container) return;
      const row = key && [...container.querySelectorAll('.cart-item')].find(item => item.dataset.itemId === key);
      let message = row?.querySelector('.cart-item__error');
      if (!message) {
        message = container.querySelector('[data-mbi-cart-error]');
        if (!message) {
          message = document.createElement('p');
          message.dataset.mbiCartError = '';
          const host = container.querySelector('.mini-cart__content') || container;
          host.prepend(message);
        }
      }
      message.setAttribute('role', 'alert');
      message.textContent = value;
      message.hidden = false;
    });
  }

  async function request(url, options = {}, format = 'json') {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 20000);
    try {
      const response = await window.fetch(url, {
        credentials: 'same-origin', cache: 'no-store', ...options, signal: controller.signal
      });
      const data = format === 'text' ? await response.text() : await response.json();
      if (!response.ok) throw new Error((typeof data === 'object' && (data.description || data.message)) || genericError());
      return data;
    } catch (error) {
      error.mbiCartStage = format === 'text' ? 'basket-html' : String(url).includes('sections=') ? 'sections-json' : String(url).includes('change.js') ? 'quantity-write' : 'cart-read';
      throw error;
    } finally {
      clearTimeout(timeout);
    }
  }

  // The drawer owns one scroller. Freeze the page as well as hiding its overflow
  // so iOS cannot move the document when a gesture starts on a fixed panel.
  function installDrawerScrolling() {
    const html = document.documentElement;
    let drawer = null;
    let saved = null;
    let frame = 0;
    let observed = [];
    let touchY = 0;
    const viewport = window.visualViewport;
    const variables = ['--mbi-cart-page-top', '--mbi-cart-page-left', '--mbi-cart-viewport-height', '--mbi-cart-viewport-top'];
    const resize = new ResizeObserver(() => schedule());
    const classes = new MutationObserver(() => schedule());

    function unlock() {
      if (!saved) return;
      const previous = saved;
      saved = null;
      html.removeAttribute('data-mbi-cart-scroll-lock');
      variables.forEach((name, index) => {
        const [value, priority] = previous.variables[index];
        if (value) html.style.setProperty(name, value, priority);
        else html.style.removeProperty(name);
      });
      const behavior = html.style.getPropertyValue('scroll-behavior');
      const priority = html.style.getPropertyPriority('scroll-behavior');
      html.style.setProperty('scroll-behavior', 'auto', 'important');
      window.scrollTo(previous.x, previous.y);
      if (behavior) html.style.setProperty('scroll-behavior', behavior, priority);
      else html.style.removeProperty('scroll-behavior');
    }

    function sync() {
      frame = 0;
      const current = document.getElementById('mini-cart');
      if (current !== drawer) {
        classes.disconnect();
        drawer = current;
        if (drawer) classes.observe(drawer, { attributes: true, attributeFilter: ['class'] });
      }
      if (!drawer?.classList.contains('open')) {
        unlock();
        resize.disconnect();
        observed = [];
        return;
      }
      if (!saved) {
        saved = { x: window.scrollX, y: window.scrollY,
          variables: variables.map(name => [html.style.getPropertyValue(name), html.style.getPropertyPriority(name)]) };
        html.style.setProperty(variables[0], -saved.y + 'px');
        html.style.setProperty(variables[1], -saved.x + 'px');
        html.setAttribute('data-mbi-cart-scroll-lock', '');
      }
      const height = viewport?.height || window.innerHeight;
      html.style.setProperty(variables[2], height + 'px');
      html.style.setProperty(variables[3], (viewport?.offsetTop || 0) + 'px');
      const sidebar = drawer.querySelector('.mini-cart__sidebar');
      const items = drawer.querySelector('.mini-cart__content');
      const footer = drawer.querySelector('.mini-cart__footer');
      if (!sidebar || !items || !footer) return;
      const top = [...items.parentElement.children].filter(node => node !== items);
      const targets = [sidebar, footer, ...top];
      if (targets.length !== observed.length || targets.some((node, i) => node !== observed[i])) {
        resize.disconnect();
        observed = targets;
        targets.forEach(node => resize.observe(node));
      }
      const topHeight = top.reduce((sum, node) => {
        const style = getComputedStyle(node);
        return sum + node.getBoundingClientRect().height + (parseFloat(style.marginTop) || 0) + (parseFloat(style.marginBottom) || 0);
      }, 0);
      // If fixed panels would consume the screen (keyboard/landscape/long voucher
      // feedback), scroll the whole drawer instead of clipping or nesting it.
      const mode = topHeight + footer.getBoundingClientRect().height > height - 96 ? 'drawer' : 'items';
      if (drawer.dataset.mbiScrollMode !== mode) drawer.dataset.mbiScrollMode = mode;
    }

    function schedule() {
      if (!frame) frame = requestAnimationFrame(sync);
    }
    function scroller() {
      return drawer?.querySelector(drawer.dataset.mbiScrollMode === 'drawer' ? '.mini-cart__sidebar' : '.mini-cart__content');
    }
    document.addEventListener('touchstart', event => {
      if (event.touches.length === 1) touchY = event.touches[0].clientY;
    }, { passive: true });
    document.addEventListener('touchmove', event => {
      if (!saved || event.touches.length !== 1) return;
      const nextY = event.touches[0].clientY;
      const delta = nextY - touchY;
      touchY = nextY;
      const host = scroller();
      const atTop = host && host.scrollTop <= 0;
      const atBottom = host && host.scrollTop + host.clientHeight >= host.scrollHeight - 1;
      if (!host?.contains(event.target) || host.scrollHeight <= host.clientHeight || (delta > 0 && atTop) || (delta < 0 && atBottom)) {
        if (event.cancelable) event.preventDefault();
      }
    }, { passive: false });
    // Wheel gestures on the fixed panels drive the same item list, never a
    // separate timeline/footer or the page underneath.
    document.addEventListener('wheel', event => {
      if (!saved || event.ctrlKey) return;
      const host = scroller();
      if (drawer?.contains(event.target) && host && !host.contains(event.target)) {
        const unit = event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? host.clientHeight : 1;
        host.scrollTop += event.deltaY * unit;
      } else if (host?.contains(event.target)) return;
      if (event.cancelable) event.preventDefault();
    }, { passive: false });
    new MutationObserver(schedule).observe(document.body, { childList: true, subtree: true });
    window.addEventListener('resize', schedule);
    viewport?.addEventListener('resize', schedule);
    viewport?.addEventListener('scroll', schedule);
    window.addEventListener('pagehide', unlock);
    window.addEventListener('pageshow', schedule);
    sync();
  }

  function overlay() {
    document.querySelectorAll(rowSelector).forEach(row => {
      const desired = queue.overlay(row.dataset.itemId);
      const input = row.querySelector('input[name="quantity"]');
      if (desired !== undefined && input) input.value = String(desired);
      if (desired !== undefined) row.setAttribute('aria-busy', String(queue.pending()));
    });
  }

  function scheduleRefresh() {
    clearTimeout(timer);
    timer = setTimeout(() => refresh().catch(() => {}), 100);
  }

  const mutationGate = createSerialGate();
  const queue = createQuantityQueue({
    exclusive: task => mutationGate.run(task),
    read: () => request(`${root}cart.js`, { headers: { Accept: 'application/json' } }),
    write: (key, quantity) => request(`${root}cart/change.js`, {
      method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: key, quantity })
    }),
    pending: () => overlay(),
    idle: scheduleRefresh,
    error: (error, quantity, key) => {
      if (error !== 'quantity') recordError(error, 'quantity-queue');
      showError(error === 'quantity'
        ? (window.cartStrings?.quantityError || genericError()).replace('[quantity]', String(quantity))
        : (error.message && error.message !== 'cart_changed' ? error.message : genericError()), key);
    }
  });

  const originalFetch = window.fetch;
  window.fetch = function (resource, options) {
    const url = typeof resource === 'string' ? resource : resource?.url || String(resource);
    const method = String(options?.method || resource?.method || 'GET').toUpperCase();
    let mutation = false;
    try {
      mutation = method !== 'GET' && new URL(url, window.location.origin).origin === window.location.origin &&
        /\/cart\/(add|change|update|clear)(\.js)?\/?$/.test(new URL(url, window.location.origin).pathname);
    } catch (_error) {}
    if (mutation) { mutations += 1; mutationRevision += 1; }
    let result;
    const finished = () => {
      if (mutation) { mutations -= 1; scheduleRefresh(); }
    };
    try { result = originalFetch.apply(this, arguments); }
    catch (error) { finished(); throw error; }
    result.then(finished, finished);
    return result;
  };

  function sectionIds() {
    const main = document.querySelector('.shopify-section.cart');
    const nav = document.querySelector('#nav-wrapper button[name="cart-open"]')?.closest('.shopify-section');
    return [...new Set([main?.id, nav?.id].filter(Boolean).map(id => id.replace(/^shopify-section-/, '')))];
  }

  function copyHtml(current, fresh) {
    if (!current || !fresh) return;
    current.innerHTML = fresh.innerHTML;
    current.querySelectorAll('.icon').forEach(icon => icon.classList.add('loaded'));
  }

  async function render() {
    await queue.idle();
    await mutationGate.idle();
    if (mutations || queue.pending() || mutationGate.pending()) return false;
    const version = queue.revision();
    const cartVersion = mutationRevision;
    const renderingVersion = ++renderRevision;
    const ids = sectionIds();
    if (!ids.length) throw new Error(genericError());
    const url = new URL(cartUrl, window.location.origin);
    url.searchParams.set('sections', ids.join(','));
    const parser = new DOMParser();
    const currentMain = document.querySelector('.shopify-section.cart');
    function candidates(documents) {
      const freshDrawer = documents.map(doc => doc.querySelector('#mini-cart')).find(Boolean);
      const freshMain = currentMain && documents.map(doc => doc.getElementById(currentMain.id)).find(Boolean);
      if (document.querySelector('#mini-cart') && !freshDrawer) throw new Error('missing_drawer_html');
      if (currentMain && !freshMain) throw new Error('missing_basket_html');
      return { documents, freshDrawer, freshMain };
    }
    let fresh;
    try {
      const sections = await request(url.href);
      fresh = candidates(ids.map(id => {
        if (typeof sections[id] !== 'string') throw new Error('missing_section_html');
        return parser.parseFromString(sections[id], 'text/html');
      }));
    } catch (error) {
      console.warn('MBI cart: using basket HTML after section refresh failed.', error.mbiCartStage || error.message);
      const html = await request(cartUrl, { headers: { Accept: 'text/html' } }, 'text');
      fresh = candidates([parser.parseFromString(html, 'text/html')]);
    }
    if (queue.pending() || mutationGate.pending() || mutations || queue.revision() !== version || cartVersion !== mutationRevision ||
        renderingVersion !== renderRevision) { scheduleRefresh(); return false; }
    const { documents, freshDrawer, freshMain } = fresh;
    const focused = document.activeElement;
    const discountFocus = focused?.matches?.('[data-mbi-discount-input]')
      ? focused.closest('[data-mbi-discount-view]')?.dataset.mbiDiscountView : null;
    const focusKey = focused?.closest?.('.cart-item')?.dataset.itemId;
    const focusName = focused?.getAttribute?.('name');
    const focusRoot = focused?.closest?.('#mini-cart') ? '#mini-cart' : '#cart';
    const drawer = document.querySelector('#mini-cart');
    copyHtml(drawer, freshDrawer);
    if (drawer && freshDrawer) {
      ['is-empty', 'mini-cart--empty'].forEach(name => drawer.classList.toggle(name, freshDrawer.classList.contains(name)));
    }
    copyHtml(currentMain, freshMain);
    const freshCount = documents.map(doc => doc.querySelector('.cart-count')).find(Boolean);
    if (freshCount) document.querySelectorAll('.cart-count').forEach(count => {
      count.innerHTML = freshCount.innerHTML;
      count.classList.toggle('visually-hidden', freshCount.classList.contains('visually-hidden'));
    });
    else document.querySelectorAll('.cart-count').forEach(count => {
      count.textContent = '0';
      count.classList.add('visually-hidden');
    });
    queue.release(version);
    showError(errorText, errorKey);
    if (focusKey && focusName) {
      [...document.querySelectorAll(`${focusRoot} .cart-item`)]
        .find(row => row.dataset.itemId === focusKey)?.querySelector(`[name="${focusName}"]`)?.focus({ preventScroll: true });
    }
    if (shouldOpen && drawer) {
      shouldOpen = false;
      if (typeof drawer.open === 'function') drawer.open();
    }
    document.dispatchEvent(new CustomEvent('mbi:cart-rendered'));
    if (discountFocus) {
      document.querySelector(`[data-mbi-discount-view="${discountFocus}"] [data-mbi-discount-input]`)?.focus({ preventScroll: true });
    }
    return true;
  }

  function recordError(error, stage) {
    const detail = { stage: error.mbiCartStage || stage, name: error.name, message: error.message };
    document.documentElement.setAttribute('data-mbi-cart-last-error', JSON.stringify(detail));
    console.error('MBI cart update failed:', detail);
  }

  function refresh(options = {}) {
    shouldOpen = shouldOpen || options.openDrawer === true;
    if (rendering) return rendering;
    const lifecycle = lifecycleRevision;
    rendering = render().catch(error => {
      if (lifecycle !== lifecycleRevision) { scheduleRefresh(); return false; }
      recordError(error, 'render');
      showError(genericError());
      throw error;
    })
      .finally(() => { rendering = null; });
    return rendering;
  }

  document.addEventListener('click', event => {
    const control = event.target.closest?.('button[name="plus"], button[name="minus"], .cart-item__remove, .cart-item-delete');
    const row = control?.closest('.cart-item');
    if (!row || !row.matches(rowSelector)) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    const input = row.querySelector('input[name="quantity"]');
    const quantity = Number(input?.value);
    const key = row.dataset.itemId;
    showError('');
    const accepted = control.matches('.cart-item__remove, .cart-item-delete')
      ? queue.remove(key, quantity)
      : queue.adjust(key, quantity, control.name === 'plus' ? 1 : -1);
    if (!accepted) { showError(genericError(), key); refresh().catch(() => {}); }
  }, true);

  document.addEventListener('click', event => {
    const checkout = event.target.closest?.('#checkout, button[name="checkout"], a[href*="/checkout"]');
    if (!checkout?.closest('#cart, #mini-cart')) return;
    try { sessionStorage.setItem('mbiCartCheckoutReturn', '1'); } catch (_error) {}
    if (!queue.pending() && !mutationGate.pending() && !mutations && !rendering) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    const selector = checkout.closest('#mini-cart') ? '#mini-cart' : '#cart';
    queue.idle().then(() => refresh()).then(applied => {
      if (!applied || errorText || queue.pending() || mutationGate.pending() || mutations) return;
      document.querySelector(selector)?.querySelector('#checkout, button[name="checkout"], a[href*="/checkout"]')?.click();
    }).catch(() => {});
  }, true);

  const resumeControls = new Set();
  const returnSync = createReturnSynchronizer({
    block: value => {
      if (value) {
        document.documentElement.setAttribute('data-mbi-cart-syncing', 'true');
        document.querySelectorAll('#mini-cart .cart-item button, #cart .cart-item button').forEach(button => {
          if (!button.disabled) { resumeControls.add(button); button.disabled = true; }
        });
      } else {
        resumeControls.forEach(button => { if (button.isConnected) button.disabled = false; });
        resumeControls.clear();
        document.documentElement.removeAttribute('data-mbi-cart-syncing');
      }
    },
    refresh: async () => {
      await queue.idle();
      await mutationGate.idle();
      if (rendering) await rendering.catch(() => {});
      const cart = await request(`${root}cart.js`, { headers: { Accept: 'application/json' } });
      queue.remember(cart);
      return refresh();
    },
    pause: () => new Promise(resolve => setTimeout(resolve, 150)),
    defer: scheduleRefresh,
    success: () => {
      showError('');
      try { sessionStorage.removeItem('mbiCartCheckoutReturn'); } catch (_error) {}
    },
    error: error => { recordError(error, 'checkout-return'); showError(genericError()); }
  });
  request(`${root}cart.js`, { headers: { Accept: 'application/json' } })
    .then(cart => queue.remember(cart)).catch(() => {});
  window.addEventListener('pagehide', () => {
    lifecycleRevision += 1;
    renderRevision += 1;
    clearTimeout(timer);
  });
  window.addEventListener('pageshow', event => {
    let fromCheckout = false;
    try { fromCheckout = /\/checkouts?(?:\/|$)/.test(new URL(document.referrer).pathname); }
    catch (_error) {}
    let checkoutExit = false;
    try { checkoutExit = sessionStorage.getItem('mbiCartCheckoutReturn') === '1'; } catch (_error) {}
    if (event.persisted || fromCheckout || checkoutExit) returnSync.sync();
  });

  new MutationObserver(() => overlay()).observe(document.documentElement, { childList: true, subtree: true });
  installDrawerScrolling();
  window.MBICart = {
    version: VERSION, refresh,
    pending: () => queue.pending() || mutationGate.pending(),
    runExclusive: task => mutationGate.run(async () => {
      const result = await task();
      if (Array.isArray(result?.items)) queue.remember(result);
      return result;
    })
  };
})();

